/**
 * Sanitizes data title input by removing special characters
 * Allows: letters, numbers, spaces, hyphens, apostrophes, and periods
 * Removes: all other special characters including underscores
 *
 * @param text - The input text to sanitize
 * @returns Sanitized text with only allowed characters
 */
export const sanitizeDataTitle = (text: string): string => {
	if (!text) return '';

	// Remove leading whitespace
	let sanitized = text.replace(/^\s+/, '');

	// Allow only: letters (any language), numbers, spaces, hyphens, apostrophes, and periods
	// This regex keeps alphanumeric characters, spaces, hyphens, apostrophes, and periods
	sanitized = sanitized.replace(/[^a-zA-Z0-9\s\-'.]/g, '');

	// Replace multiple consecutive spaces with a single space
	sanitized = sanitized.replace(/\s{2,}/g, ' ');

	return sanitized;
};

/**
 * Validates if a data title contains only allowed characters
 *
 * @param text - The text to validate
 * @returns true if valid, false if contains special characters
 */
export const isValidDataTitle = (text: string): boolean => {
	if (!text) return false;

	// Check if the text contains only allowed characters
	const validPattern = /^[a-zA-Z0-9\s\-'.]+$/;
	return validPattern.test(text.trim());
};

/**
 * Gets a user-friendly error message for invalid characters in data title
 *
 * @param text - The text that failed validation
 * @returns Error message string
 */
export const getDataTitleValidationError = (text: string): string => {
	if (!text) return 'Title is required';

	// Find invalid characters
	const invalidChars = text.match(/[^a-zA-Z0-9\s\-'.]/g);

	if (invalidChars && invalidChars.length > 0) {
		const uniqueInvalidChars = [...new Set(invalidChars)];
		return `Special characters not allowed: ${uniqueInvalidChars.join(', ')}. Only letters, numbers, spaces, hyphens, apostrophes, and periods are permitted.`;
	}

	return 'Title contains invalid characters';
};

export default sanitizeDataTitle;
